import React, { useEffect, useState } from "react";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RotateCw, Loader2, Clapperboard, CheckCircle2, XCircle, Clock, Film, Scissors, ChevronUp, ChevronDown, Eye, EyeOff, Sparkles } from "lucide-react";

const CAMERA_MOVES = [
  { v: "push_in", l: "Slow push-in" },
  { v: "pull_out", l: "Slow pull-out" },
  { v: "pan_left", l: "Pan left" },
  { v: "pan_right", l: "Pan right" },
  { v: "tilt_up", l: "Tilt up (reveal)" },
  { v: "tilt_down", l: "Tilt down" },
  { v: "orbit", l: "Orbit / arc" },
  { v: "crane_up", l: "Crane up" },
  { v: "dolly", l: "Dolly through" },
  { v: "hold_zoom", l: "Hold, then slow zoom" },
  { v: "static", l: "Static / locked-off" },
];

const motionLabel = (s) => {
  const byKey = CAMERA_MOVES.find((m) => m.v === s.camera);
  if (byKey) return byKey.l;
  const mp = (s.motion_prompt || "").trim();
  if (!mp) return "Auto (AI-chosen)";
  return mp.length > 48 ? mp.slice(0, 48) + "…" : mp;
};

const StatusChip = ({ status }) => {
  const map = {
    done: { c: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", i: CheckCircle2 },
    running: { c: "text-amber-400 border-amber-500/40 bg-amber-500/10", i: Loader2 },
    submitting: { c: "text-amber-400 border-amber-500/40 bg-amber-500/10", i: Loader2 },
    failed: { c: "text-red-400 border-red-500/40 bg-red-500/10", i: XCircle },
    pending: { c: "text-zinc-500 border-zinc-700 bg-zinc-800/40", i: Clock },
  };
  const m = map[status] || map.pending;
  const I = m.i;
  const spin = status === "running" || status === "submitting";
  return (
    <span className={`inline-flex items-center gap-1.5 font-mono2 text-[9px] uppercase tracking-widest px-2 py-1 border rounded-sm ${m.c}`}>
      <I className={`w-3 h-3 ${spin ? "animate-spin" : ""}`} /> {status}
    </span>
  );
};

export default function ClipsPanel({ project, onChange, onRender }) {
  const isDealership = (project.vertical || project.settings?.vertical) === "dealership";
  const cameraMoves = isDealership
    ? [...CAMERA_MOVES,
       { v: "front_3q", l: "Front 3/4 hero" },
       { v: "wheel_detail", l: "Wheel detail" },
       { v: "badge_closeup", l: "Badge close-up" },
       { v: "driving", l: "Driving (with driver)" }]
    : CAMERA_MOVES;

  const [gen, setGen] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const [cam, setCam] = useState({});
  const [dir, setDir] = useState({});
  const [avoidTxt, setAvoidTxt] = useState({});
  const [voices, setVoices] = useState([]);
  const [voiceName, setVoiceName] = useState(project.settings?.voice_name || "");
  const [previewing, setPreviewing] = useState(false);
  const [revoicing, setRevoicing] = useState(false);
  const [targetRes, setTargetRes] = useState(project.settings?.resolution || "720");
  const [targetModel, setTargetModel] = useState(project.settings?.model || "mini");
  const [requalifying, setRequalifying] = useState(false);
  const [cutStyle, setCutStyle] = useState(project.settings?.transition || "dip");
  const [savingCut, setSavingCut] = useState(false);
  const [motionMode, setMotionMode] = useState(project.settings?.motion_mode || "glide");
  const [savingMotion, setSavingMotion] = useState(false);
  const [capSize, setCapSize] = useState(project.settings?.captionSize || "medium");
  const [savingCap, setSavingCap] = useState(false);
  const [wtStart, setWtStart] = useState("");
  const [wtEnd, setWtEnd] = useState("");
  const [wtAfter, setWtAfter] = useState("");
  const [wtBusy, setWtBusy] = useState(false);

  const saveCapSize = async (v) => {
    setCapSize(v);
    setSavingCap(true);
    try {
      await api.post(`/projects/${project.id}/settings`, { captionSize: v });
      toast.success("Caption size saved — re-render to apply it.");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingCap(false);
    }
  };

  const addWalkthrough = async () => {
    if (!wtStart || !wtEnd) return toast.error("Pick a start photo and an end photo");
    if (wtStart === wtEnd) return toast.error("Start and end photos must be different");
    setWtBusy(true);
    try {
      await api.post(`/projects/${project.id}/transition`, {
        start_media_id: wtStart, end_media_id: wtEnd,
        after_index: wtAfter === "" ? (ordered()[0]?.index ?? 0) : parseInt(wtAfter, 10),
      });
      toast.success("Walk-through clip created — re-render to see it.");
      setWtStart(""); setWtEnd(""); setWtAfter("");
      setTimeout(() => { load(); onChange && onChange(); }, 800);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setWtBusy(false);
    }
  };

  const load = () => api.get(`/projects/${project.id}/generation`).then((r) => setGen(r.data && r.data.scenes ? r.data : null)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [project.id]);

  useEffect(() => {
    api.get("/projects/voices").then((r) => {
      const vs = r.data.voices || [];
      setVoices(vs);
      const keys = new Set(vs.map((v) => v.key));
      const stored = project.settings?.voice_name;
      setVoiceName(keys.has(stored) ? stored : (r.data.default || vs[0]?.key || ""));
    }).catch(() => {});
  }, [project.id]);

  const previewVoice = async () => {
    if (!voiceName) return;
    setPreviewing(true);
    try {
      const res = await api.post("/projects/voices/preview",
        { voice: voiceName, text: "Welcome to this beautiful home. Let me show you around." },
        { responseType: "blob" });
      new Audio(URL.createObjectURL(res.data)).play();
    } catch (err) {
      toast.error("Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const revoice = async () => {
    if (!voiceName) return;
    setRevoicing(true);
    try {
      await api.post(`/projects/${project.id}/revoice`, { voice_name: voiceName });
      toast.success("Applying new voice to all narration…");
      setTimeout(() => { load(); onChange && onChange(); }, 1000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRevoicing(false);
    }
  };

  const applyMotion = async () => {
    setSavingMotion(true);
    try {
      await api.post(`/projects/${project.id}/settings`, { motion_mode: motionMode });
      await api.post(`/projects/${project.id}/regenerate-clips`);
      toast.success(motionMode === "ai"
        ? "Regenerating clips with AI motion…"
        : "Rebuilding clips from your real photos — no AI redraw, free.");
      setTimeout(() => { load(); onChange && onChange(); }, 1000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingMotion(false);
    }
  };

  const regenerateAll = async () => {
    try {
      await api.post(`/projects/${project.id}/regenerate-clips`);
      toast.success("Regenerating all clips (smooth, narration-matched)…");
      setTimeout(() => { load(); onChange && onChange(); }, 1000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const requalify = async () => {
    setRequalifying(true);
    try {
      const r = await api.post(`/projects/${project.id}/requalify`, { resolution: targetRes, model: targetModel });
      toast.success(r.data?.message || "Regenerating kept clips at new quality…");
      setTimeout(() => { load(); onChange && onChange(); }, 1000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRequalifying(false);
    }
  };

  const saveCutStyle = async (v) => {
    setCutStyle(v);
    setSavingCut(true);
    try {
      await api.post(`/projects/${project.id}/settings`, { transition: v });
      toast.success("Cut style saved — re-render to apply it.");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingCut(false);
    }
  };

  const ordered = () => [...(gen?.scenes || [])].sort((a, b) => (a.order ?? a.index) - (b.order ?? b.index));

  const move = async (index, dir) => {
    const list = ordered();
    const pos = list.findIndex((s) => s.index === index);
    const swap = pos + dir;
    if (swap < 0 || swap >= list.length) return;
    const tmp = list[pos]; list[pos] = list[swap]; list[swap] = tmp;
    const newOrder = list.map((s) => s.index);
    setGen({ ...gen, scenes: list.map((s, i) => ({ ...s, order: i })) });
    try {
      await api.post(`/projects/${project.id}/generation/reorder`, { order: newOrder });
      onChange && onChange();
    } catch (err) { toast.error("Reorder failed"); load(); }
  };

  const toggleScene = async (index, enabled) => {
    setGen({ ...gen, scenes: gen.scenes.map((s) => s.index === index ? { ...s, enabled } : s) });
    try {
      await api.post(`/projects/${project.id}/generation/${index}/toggle`, { enabled });
      onChange && onChange();
    } catch (err) { toast.error("Toggle failed"); load(); }
  };

  const retry = async (index) => {
    setRetrying(index);
    try {
      await api.post(`/projects/${project.id}/generation/${index}/retry`);
      toast.success(`Retrying scene ${index + 1}`);
      setTimeout(load, 800);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRetrying(null);
    }
  };

  const recut = async (index) => {
    const camera = cam[index] ?? "";
    const direction = (dir[index] ?? "").trim();
    const avoid = (avoidTxt[index] ?? "").trim();
    if (!camera && !direction && !avoid)
      return toast.error("Pick a camera move or add a direction first");
    const isSetup = project.status === "setup_clips";
    setRetrying(index);
    try {
      await api.post(`/projects/${project.id}/generation/${index}/recut`, { camera, direction, avoid, apply: !isSetup });
      toast.success(isSetup ? `Shot ${index + 1} camera set` : `Re-cutting scene ${index + 1} with your direction`);
      setTimeout(load, 800);
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRetrying(null);
    }
  };

  const generateClips = async () => {
    setRetrying("all");
    try {
      await api.post(`/projects/${project.id}/scenes/confirm`);
      toast.success("Generating your clips with the shots you set…");
      setTimeout(() => { load(); onChange && onChange(); }, 900);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRetrying(null);
    }
  };

  if (!gen)
    return (
      <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center text-zinc-600" data-testid="clips-empty">
        Clips appear here once generation starts.
      </div>
    );

  const done = gen.scenes.filter((s) => s.status === "done").length;
  const busy = gen.scenes.some((s) => s.status === "running" || s.status === "submitting");
  const reviewing = project.status === "review_clips";
  const setup = project.status === "setup_clips";
  const hasClips = (gen?.scenes || []).some((s) => s.status === "done");

  return (
    <div className="fade-up" data-testid="clips-panel">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 text-amber-500">
          <Clapperboard className="w-4 h-4" />
          <span className="font-mono2 text-[10px] uppercase tracking-[0.2em]">
            {setup ? `Set up shots — ${gen.scenes.length} scenes` : `Scene Clips — ${done}/${gen.scenes.length} done`}
          </span>
        </div>
        {setup && (
          <Button
            data-testid="generate-clips-button"
            onClick={generateClips}
            disabled={retrying === "all"}
            className="h-9 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50"
          >
            {retrying === "all" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Film className="w-4 h-4 mr-2" />}
            Generate clips
          </Button>
        )}
        {reviewing && (
          <div className="flex items-center gap-2">
            <Button
              data-testid="regenerate-all-button"
              onClick={regenerateAll}
              disabled={busy}
              variant="outline"
              className="h-9 rounded-sm border-zinc-700 text-zinc-200 hover:bg-zinc-800 disabled:opacity-50"
            >
              <RotateCw className="w-4 h-4 mr-2" /> Regenerate all (smooth)
            </Button>
            <Button
              data-testid="clips-render-button"
              onClick={() => onRender && onRender()}
              disabled={busy}
              className="h-9 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50"
            >
              <Film className="w-4 h-4 mr-2" />
              {busy ? "Waiting for clips…" : "Render Final Video"}
            </Button>
          </div>
        )}
      </div>
      {reviewing && (
        <p className="text-xs text-zinc-500 mb-4" data-testid="clips-review-hint">
          Preview each clip. Not happy with one? Choose a camera movement and re-cut it. When everything looks right, render the final video.
        </p>
      )}
      {setup && (
        <p className="text-xs text-zinc-500 mb-4" data-testid="clips-setup-hint">
          Set the camera move for each shot before anything is generated — this is free. The AI has suggested one for each; change any you like, then hit <span className="text-amber-400">Generate clips</span>. You only pay to generate once.
        </p>
      )}

      {(reviewing || setup || hasClips) && (
        <div className="mb-5 border border-amber-500/40 bg-amber-500/[0.04] rounded-sm p-3" data-testid="motion-bar">
          <p className="font-mono2 text-[9px] uppercase tracking-widest text-amber-400/80 mb-2">
            Photo Motion — keep your real photos unchanged
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              data-testid="motion-bar-select"
              value={motionMode}
              onChange={(e) => setMotionMode(e.target.value)}
              className="flex-1 min-w-[220px] h-9 rounded-sm bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 focus:border-amber-500 outline-none"
            >
              <option value="glide">Faithful glide (free) — gentle push-in, nothing redrawn</option>
              <option value="static">Faithful still (free) — photo held, zero movement</option>
              <option value="ai">AI motion (Seedance) — REDRAWS the photo (subject may change)</option>
            </select>
            <Button
              data-testid="motion-bar-apply"
              onClick={applyMotion}
              disabled={savingMotion || busy || motionMode === (project.settings?.motion_mode || "glide")}
              size="sm"
              className="h-9 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 disabled:opacity-50"
            >
              {savingMotion ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RotateCw className="w-3.5 h-3.5 mr-1.5" /> Apply &amp; rebuild clips</>}
            </Button>
          </div>
          <p className="text-[11px] text-zinc-600 mt-2">
            Currently <span className="text-amber-400">{{ glide: "Faithful glide", static: "Faithful still", ai: "AI motion" }[project.settings?.motion_mode || "glide"]}</span>.
            Switching to a Faithful mode rebuilds every clip straight from your uploaded photos (no AI, no credits) so the subject stays pixel-identical.
          </p>
        </div>
      )}

      <div className="mb-5 border border-zinc-800 bg-zinc-900/40 rounded-sm p-3" data-testid="revoice-bar">
        <p className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500 mb-2">Narration Voice</p>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            data-testid="revoice-select"
            value={voiceName}
            onChange={(e) => setVoiceName(e.target.value)}
            className="flex-1 min-w-[180px] h-9 rounded-sm bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 focus:border-amber-500 outline-none"
          >
            {voices.length === 0 && <option value="">Loading voices…</option>}
            {voices.map((v) => (
              <option key={v.key} value={v.key}>{v.name}{v.desc ? ` — ${v.desc}` : ""}</option>
            ))}
          </select>
          <Button data-testid="revoice-preview" onClick={previewVoice} disabled={previewing || !voiceName}
            size="sm" className="h-9 rounded-sm bg-zinc-800 text-zinc-100 hover:bg-zinc-700 disabled:opacity-50">
            {previewing ? "Playing…" : "▶ Preview"}
          </Button>
          <Button data-testid="revoice-apply" onClick={revoice} disabled={revoicing || busy || !voiceName}
            size="sm" className="h-9 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 disabled:opacity-50">
            {revoicing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Apply new voice"}
          </Button>
        </div>
        <p className="text-[11px] text-zinc-600 mt-2">Re-records all narration in the chosen voice (keeps your clips). Re-render when done.</p>
      </div>

      {reviewing && (
        <div className="mb-5 border border-zinc-800 bg-zinc-900/40 rounded-sm p-3" data-testid="requalify-bar">
          <p className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500 mb-2">
            Clip Quality &amp; Model — currently {project.settings?.resolution || "720"}p
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              data-testid="requalify-model-select"
              value={targetModel}
              onChange={(e) => setTargetModel(e.target.value)}
              className="flex-1 min-w-[180px] h-9 rounded-sm bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 focus:border-amber-500 outline-none"
            >
              <option value="mini">Seedance 2.0 Mini — fastest/cheapest</option>
              <option value="fast">Seedance 2.0 Fast</option>
              <option value="pro">Seedance 2.0 Pro — best 2.0</option>
              <option value="v25">Seedance 2.5 — newest</option>
              <option value="veo">Google Veo 3.1 (8s, beta)</option>
            </select>
            <select
              data-testid="requalify-select"
              value={targetRes}
              onChange={(e) => setTargetRes(e.target.value)}
              className="flex-1 min-w-[160px] h-9 rounded-sm bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 focus:border-amber-500 outline-none"
            >
              <option value="480">480p — draft (cheapest)</option>
              <option value="720">720p — standard</option>
              <option value="1080">1080p — sharpest (most credits)</option>
            </select>
            <Button
              data-testid="requalify-apply"
              onClick={requalify}
              disabled={requalifying || busy}
              size="sm"
              className="h-9 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 disabled:opacity-50"
            >
              {requalifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Regenerate kept clips</>}
            </Button>
          </div>
          <p className="text-[11px] text-zinc-600 mt-2">
            Regenerates only the <span className="text-emerald-400">Included</span> clips with the chosen model &amp; quality — Skipped clips and the agent intro are left as-is, so you don't spend credits on what you dropped. Re-render when done.
          </p>
        </div>
      )}

      {reviewing && (
        <div className="mb-5 border border-zinc-800 bg-zinc-900/40 rounded-sm p-3" data-testid="cutstyle-bar">
          <p className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500 mb-2">Cut Style — between clips</p>
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { v: "dip", label: "Dip to Black" },
              { v: "hard", label: "Hard Cut" },
              { v: "dissolve", label: "Smooth Dissolve" },
            ].map((t) => (
              <button
                key={t.v}
                data-testid={`cutstyle-${t.v}`}
                disabled={savingCut}
                onClick={() => saveCutStyle(t.v)}
                className={`font-mono2 text-[10px] uppercase tracking-[0.15em] rounded-sm px-3 py-2 border transition-colors disabled:opacity-50 ${
                  cutStyle === t.v
                    ? "border-amber-500 bg-amber-500/10 text-amber-500"
                    : "border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-zinc-600 mt-2">
            Fixes the "ghost" blend — <span className="text-zinc-300">Dip to Black</span> and <span className="text-zinc-300">Hard Cut</span> have no double-image. Applies on your next render (no credits).
          </p>
        </div>
      )}

      {hasClips && (
        <div className="mb-5 border border-zinc-800 bg-zinc-900/40 rounded-sm p-3" data-testid="captionsize-bar">
          <p className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500 mb-2">Caption Size</p>
          <div className="flex items-center gap-2 flex-wrap">
            {[{ v: "small", label: "Small" }, { v: "medium", label: "Medium" }, { v: "large", label: "Large" }].map((t) => (
              <button
                key={t.v}
                data-testid={`captionsize-${t.v}`}
                disabled={savingCap}
                onClick={() => saveCapSize(t.v)}
                className={`font-mono2 text-[10px] uppercase tracking-[0.15em] rounded-sm px-3 py-2 border transition-colors disabled:opacity-50 ${
                  capSize === t.v ? "border-amber-500 bg-amber-500/10 text-amber-500" : "border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-zinc-600 mt-2">Sets the on-screen caption text size. Applies on your next render (no credits).</p>
        </div>
      )}

      {hasClips && (
        <div className="mb-5 border border-zinc-800 bg-zinc-900/40 rounded-sm p-3" data-testid="walkthrough-bar">
          <p className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500 mb-2">Walk-through Transition — move through a doorway</p>
          <div className="grid sm:grid-cols-3 gap-2">
            <select data-testid="wt-start" value={wtStart} onChange={(e) => setWtStart(e.target.value)}
              className="h-9 rounded-sm bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 focus:border-amber-500 outline-none">
              <option value="">Start photo (e.g. entrance)…</option>
              {ordered().filter((s) => s.media_id).map((s) => (
                <option key={`ws${s.index}`} value={s.media_id}>Scene {s.index + 1}</option>
              ))}
            </select>
            <select data-testid="wt-end" value={wtEnd} onChange={(e) => setWtEnd(e.target.value)}
              className="h-9 rounded-sm bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 focus:border-amber-500 outline-none">
              <option value="">End photo (e.g. interior)…</option>
              {ordered().filter((s) => s.media_id).map((s) => (
                <option key={`we${s.index}`} value={s.media_id}>Scene {s.index + 1}</option>
              ))}
            </select>
            <select data-testid="wt-after" value={wtAfter} onChange={(e) => setWtAfter(e.target.value)}
              className="h-9 rounded-sm bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 focus:border-amber-500 outline-none">
              <option value="">Insert after…</option>
              {ordered().map((s) => (
                <option key={`wa${s.index}`} value={s.index}>{s.type === "agent_intro" ? "Agent Intro" : `Scene ${s.index + 1}`}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {wtStart && <img src={mediaUrl(wtStart)} alt="start" className="h-10 w-16 object-cover rounded-sm" />}
            {wtStart && wtEnd && <span className="text-amber-500 text-lg">→</span>}
            {wtEnd && <img src={mediaUrl(wtEnd)} alt="end" className="h-10 w-16 object-cover rounded-sm" />}
            <Button data-testid="wt-generate" onClick={addWalkthrough} disabled={wtBusy}
              size="sm" className="h-9 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 disabled:opacity-50 ml-auto">
              {wtBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generate walk-through (8s)</>}
            </Button>
          </div>
          <p className="text-[11px] text-zinc-600 mt-2">
            Generates one continuous clip that moves from the start photo INTO the end photo (uses AI credits). Works best when the start photo has an open door. Re-render when done.
          </p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {ordered().map((s) => (
          <div key={s.index} className={`bg-zinc-900 border rounded-sm overflow-hidden ${s.enabled === false ? "border-zinc-800 opacity-50" : "border-zinc-800"}`} data-testid={`clip-${s.index}`}>
            <div className="relative aspect-video bg-zinc-950">
              {s.status === "done" && s.clip_media_id ? (
                <video src={mediaUrl(s.clip_media_id)} controls preload="metadata" poster={s.media_id ? mediaUrl(s.media_id) : undefined} className="w-full h-full object-contain" data-testid={`clip-video-${s.index}`} />
              ) : (
                <>
                  {s.media_id && <img src={mediaUrl(s.media_id)} alt="" className="w-full h-full object-cover opacity-40" />}
                  <div className="absolute inset-0 flex items-center justify-center">
                    {(s.status === "running" || s.status === "submitting") && (
                      <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono2 text-[10px] uppercase tracking-widest text-zinc-500">
                  {s.type === "agent_intro" ? "Agent Intro" : `Scene ${s.index + 1}`}
                  {s.actual_duration ? ` · ${s.actual_duration.toFixed(1)}s` : ""}
                </span>
                <StatusChip status={s.status} />
              </div>
              {s.caption && <p className="mt-2 text-sm text-zinc-300 line-clamp-2">{s.caption}</p>}
              {s.error && <p className="mt-2 text-xs text-red-400 line-clamp-2">{s.error}</p>}

              {s.type !== "agent_intro" && (
                <div className="mt-3 flex items-center gap-2" data-testid={`arrange-${s.index}`}>
                  <button data-testid={`move-up-${s.index}`} onClick={() => move(s.index, -1)}
                    className="h-7 w-7 flex items-center justify-center rounded-sm border border-zinc-700 text-zinc-400 hover:text-amber-400 hover:border-amber-500/50">
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button data-testid={`move-down-${s.index}`} onClick={() => move(s.index, 1)}
                    className="h-7 w-7 flex items-center justify-center rounded-sm border border-zinc-700 text-zinc-400 hover:text-amber-400 hover:border-amber-500/50">
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <button data-testid={`toggle-${s.index}`} onClick={() => toggleScene(s.index, s.enabled === false)}
                    className={`h-7 px-2 flex items-center gap-1 rounded-sm border text-[11px] font-medium ${s.enabled === false ? "border-zinc-700 text-zinc-500" : "border-emerald-600/40 text-emerald-400"}`}>
                    {s.enabled === false ? <><EyeOff className="w-3.5 h-3.5" /> Skipped</> : <><Eye className="w-3.5 h-3.5" /> Included</>}
                  </button>
                </div>
              )}

              {s.type !== "agent_intro" && (
                <div className="mt-3" data-testid={`recut-controls-${s.index}`}>
                  <p className="font-mono2 text-[9px] uppercase tracking-widest text-zinc-500 mb-1.5">
                    Camera: <span className="text-amber-400/90">{motionLabel(s)}</span>
                  </p>
                  <div className="flex items-center gap-2">
                    <select
                      data-testid={`camera-select-${s.index}`}
                      value={cam[s.index] ?? s.camera ?? ""}
                      onChange={(e) => setCam({ ...cam, [s.index]: e.target.value })}
                      className="flex-1 h-8 rounded-sm bg-zinc-950 border border-zinc-800 text-xs text-zinc-300 px-2 focus:border-amber-500 outline-none"
                    >
                      <option value="">Change camera movement…</option>
                      {cameraMoves.map((m) => (
                        <option key={m.v} value={m.v}>{m.l}</option>
                      ))}
                    </select>
                    <Button
                      data-testid={`recut-${s.index}`}
                      onClick={() => recut(s.index)}
                      disabled={retrying === s.index || !((cam[s.index] ?? s.camera) || (dir[s.index] ?? "").trim() || (avoidTxt[s.index] ?? "").trim())}
                      size="sm"
                      className="h-8 rounded-sm bg-amber-500/90 text-zinc-950 font-semibold hover:bg-amber-400 disabled:opacity-40"
                    >
                      {retrying === s.index ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Scissors className="w-3.5 h-3.5 mr-1" /> {setup ? "Set move" : "Re-cut"}</>}
                    </Button>
                  </div>
                  <div className="mt-2 grid gap-2" data-testid={`direct-shot-${s.index}`}>
                    <textarea
                      data-testid={`direction-${s.index}`}
                      value={dir[s.index] ?? s.direction ?? ""}
                      onChange={(e) => setDir({ ...dir, [s.index]: e.target.value })}
                      rows={2}
                      placeholder="✅ Do this — e.g. keep the camera still, show the whole kitchen, slower move"
                      className="w-full rounded-sm bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 py-1.5 focus:border-emerald-500/60 outline-none resize-none placeholder:text-zinc-600"
                    />
                    <textarea
                      data-testid={`avoid-${s.index}`}
                      value={avoidTxt[s.index] ?? s.avoid ?? ""}
                      onChange={(e) => setAvoidTxt({ ...avoidTxt, [s.index]: e.target.value })}
                      rows={2}
                      placeholder="🚫 Avoid this — e.g. no zooming on faces, no warping, don't move furniture"
                      className="w-full rounded-sm bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 py-1.5 focus:border-red-500/50 outline-none resize-none placeholder:text-zinc-600"
                    />
                    <p className="text-[10px] text-zinc-600 leading-snug">
                      Type what to do / avoid, then Re-cut to regenerate just this clip with your direction. Tweak the wording and re-cut again until it's right.
                    </p>
                  </div>
                </div>
              )}

              {(s.status === "failed" || s.status === "done") && (
                <Button
                  data-testid={`retry-${s.index}`}
                  onClick={() => retry(s.index)}
                  disabled={retrying === s.index}
                  variant="ghost"
                  size="sm"
                  className="mt-2 h-8 rounded-sm text-zinc-400 hover:text-amber-400 group"
                >
                  <RotateCw className={`w-3.5 h-3.5 mr-1.5 transition-transform group-hover:rotate-180`} /> Regenerate (same motion)
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
